"use client"; // Indicate this is a client component

import { useUser } from "@clerk/clerk-react";
import { api } from "@convex/_generated/api"; // Use alias
import { useMutation, useQuery } from "convex/react";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import PageLayout from "@/components/_shared/PageLayout";
import QRCodeModal from "@/components/_shared/QRCodeModal";
import Toast from "@/components/_shared/Toast";
import { RaumEditor } from "@/components/text-share/RaumEditor";
import { RaumEinstellungen } from "@/components/text-share/RaumEinstellungen";
import { RaumPasswort } from "@/components/text-share/RaumPasswort";
import { RaumStatusLeiste } from "@/components/text-share/RaumStatusLeiste";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// import { Button } from '@/components/ui/button'; // entfernt: Header-Button nicht mehr genutzt

// TinyMCE API-Key (ensure this is handled securely in production)
const TINYMCE_API_KEY = import.meta.env?.VITE_TINYMCE_API_KEY as string;

export default function TextShareRoomPage() {
	const [, navigate] = useLocation();
	const params = useParams<{ roomId?: string }>();
	const raumId = params?.roomId || "";

	// State variables
	const [ladeVorgang, setLadeVorgang] = useState(true);
	const [inhalt, setInhalt] = useState("");
	const [passwort, setPasswort] = useState(""); // Actual password if owner
	const [eingegebenesPasswort, setEingegebenesPasswort] = useState(""); // User input
	const [nurBesitzerEdit, setNurBesitzerEdit] = useState(true);
	const [istBesitzer, setIstBesitzer] = useState(false);
	const [kannBearbeiten, setKannBearbeiten] = useState(false);
	const [passwortModalAnzeigen, setPasswortModalAnzeigen] = useState(false);
	const [einstellungenModalAnzeigen, setEinstellungenModalAnzeigen] =
		useState(false);
	const [erfolgsMeldung, setErfolgsMeldung] = useState<string | null>(null);
	const [fehlerMeldung, setFehlerMeldung] = useState<string | null>(null);
	const [speicherStatus, setSpeicherStatus] = useState<
		"bereit" | "speichert" | "gespeichert" | "fehler"
	>("bereit");
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [qrLink, setQrLink] = useState("");
	const [aufrufGezaehlt, setAufrufGezaehlt] = useState(false);
	const [zugangGenehmigt, setZugangGenehmigt] = useState(false);
	const [passwortVersucht, setPasswortVersucht] = useState(false);
	const [initialePruefungErfolgt, setInitialePruefungErfolgt] = useState(false);
	const [ablaufDialogAnzeigen, setAblaufDialogAnzeigen] = useState(false);

	// Convex Queries and Mutations
	const pruefRaumQuery = useQuery(
		api.textShare.raumPasswortPruefen,
		initialePruefungErfolgt || !raumId ? "skip" : { raumId },
	);
	const raumQuery = useQuery(
		api.textShare.raumLaden,
		zugangGenehmigt && raumId
			? {
					raumId,
					...(eingegebenesPasswort ? { passwort: eingegebenesPasswort } : {}),
				}
			: "skip",
	);
	const raumInhaltAktualisierenMutation = useMutation(
		api.textShare.raumInhaltAktualisieren,
	);
	const raumEinstellungenAktualisierenMutation = useMutation(
		api.textShare.raumEinstellungenAktualisieren,
	);
	const aufrufZaehlenMutation = useMutation(api.textShare.aufrufZaehlen);
	const raumLoeschenMutation = useMutation(api.textShare.raumLoeschen);

	// Clerk User verwenden statt nutzerHash
	const { user } = useUser();

	// Initial check for password requirement
	useEffect(() => {
		if (initialePruefungErfolgt || !raumId) return;

		if (pruefRaumQuery === undefined) {
			setLadeVorgang(true);
			return;
		}

		setLadeVorgang(false);

		if (pruefRaumQuery === null) {
			setAblaufDialogAnzeigen(true);
			return;
		}

		setInitialePruefungErfolgt(true);
		if (pruefRaumQuery.hatPasswort) {
			setPasswortModalAnzeigen(true);
		} else {
			setZugangGenehmigt(true);
		}
	}, [pruefRaumQuery, initialePruefungErfolgt, raumId]);

	// Process room data after access is granted
	useEffect(() => {
		if (!(zugangGenehmigt && raumId)) return;

		if (raumQuery === undefined) {
			setLadeVorgang(true);
			return;
		}

		setLadeVorgang(false);

		if (raumQuery === null) {
			setAblaufDialogAnzeigen(true);
			return;
		}

		// Check if password was required but incorrect (content is missing)
		if (raumQuery.hatPasswort && !raumQuery.inhalt && passwortVersucht) {
			setFehlerMeldung("Falsches Passwort. Bitte versuche es erneut.");
			setZugangGenehmigt(false);
			setPasswortVersucht(false);
			setPasswortModalAnzeigen(true);
			return;
		}

		// Process valid room data
		if (raumQuery.inhalt !== undefined) {
			setInhalt(raumQuery.inhalt);
			// Prüfen, ob der aktuelle Benutzer der Besitzer ist
			const raumBesitzer =
				!!user && !!raumQuery.clerkUserId && user.id === raumQuery.clerkUserId;
			setIstBesitzer(raumBesitzer);
			setKannBearbeiten(!raumQuery.nurBesitzerEdit || raumBesitzer);
			setPasswort(raumQuery.passwort || ""); // Store actual password if owner/correct pw entered
			setNurBesitzerEdit(raumQuery.nurBesitzerEdit);

			if (!aufrufGezaehlt) {
				aufrufZaehlenMutation({ raumId }).catch(() => {}); // Fire and forget
				setAufrufGezaehlt(true);
			}
		}
	}, [
		raumQuery,
		raumId,
		user,
		aufrufZaehlenMutation,
		aufrufGezaehlt,
		zugangGenehmigt,
		passwortVersucht,
	]);

	// Auto-hide toasts
	useEffect(() => {
		let timer: NodeJS.Timeout | null = null;
		if (erfolgsMeldung) {
			timer = setTimeout(() => setErfolgsMeldung(null), 3000);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [erfolgsMeldung]);

	useEffect(() => {
		let timer: NodeJS.Timeout | null = null;
		if (fehlerMeldung) {
			timer = setTimeout(() => setFehlerMeldung(null), 3000);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [fehlerMeldung]);

	// Handle password submission
	const handlePasswortPruefen = () => {
		if (!eingegebenesPasswort) {
			setFehlerMeldung("Bitte gib das Passwort ein.");
			return;
		}
		setLadeVorgang(true);
		setPasswortVersucht(true);
		setPasswortModalAnzeigen(false);
		setZugangGenehmigt(true); // Attempt to load full data with password
	};

	// Copy link
	const linkKopieren = () => {
		const url = window.location.href;
		navigator.clipboard.writeText(url).then(
			() => setErfolgsMeldung("Link kopiert!"),
			() => setFehlerMeldung("Kopieren fehlgeschlagen."),
		);
	};

	// Show QR code
	const handleQrAnzeigen = () => {
		setQrLink(window.location.href);
		setQrModalOpen(true);
	};

	// Save content changes
	const handleInhaltSpeichern = async () => {
		if (!(kannBearbeiten && raumId)) return;
		setSpeicherStatus("speichert");
		try {
			await raumInhaltAktualisierenMutation({ raumId, inhalt });
			setSpeicherStatus("gespeichert");
			setTimeout(() => setSpeicherStatus("bereit"), 2000);
		} catch (error) {
			setSpeicherStatus("fehler");
			setFehlerMeldung("Fehler beim Speichern.");
			setTimeout(() => setSpeicherStatus("bereit"), 2000);
		}
	};

	// Update room settings
	const handleEinstellungenAktualisieren = async () => {
		if (!(istBesitzer && raumId)) return;
		try {
			await raumEinstellungenAktualisierenMutation({
				raumId,
				passwort: passwort.trim() || undefined, // Use the state holding the potential new password
				nurBesitzerEdit,
			});
			setEinstellungenModalAnzeigen(false);
			setErfolgsMeldung("Einstellungen aktualisiert.");
		} catch (error) {
			setFehlerMeldung("Fehler beim Aktualisieren.");
		}
	};

	// Ablauf-Reset entfernt

	// Navigate back if room expired/not found
	const handleAblaufRoom = () => {
		setAblaufDialogAnzeigen(false);
		navigate("/text");
	};

	// Toggle edit permissions (owner only)
	const handleToggleNurBesitzerEdit = async () => {
		if (!(istBesitzer && raumId)) return;
		const neuerWert = !nurBesitzerEdit;
		try {
			await raumEinstellungenAktualisierenMutation({
				raumId,
				passwort: passwort.trim() || undefined,
				nurBesitzerEdit: neuerWert,
			});
			setNurBesitzerEdit(neuerWert); // Update local state on success
			setErfolgsMeldung(
				`Bearbeitungsrechte: ${neuerWert ? "Nur Besitzer" : "Alle"}`,
			);
		} catch (error) {
			setFehlerMeldung("Fehler beim Ändern der Rechte.");
		}
	};

	// Set document title
	useEffect(() => {
		document.title = raumId ? `TextShare Raum: ${raumId}` : "TextShare Raum";
	}, [raumId]);

	const handleDeleteClick = async () => {
		if (!(istBesitzer && raumId)) return;
		const bestaetigt = window.confirm("Diesen Raum wirklich löschen?");
		if (!bestaetigt) return;
		try {
			await raumLoeschenMutation({ raumId });
			setErfolgsMeldung("Raum gelöscht.");
			navigate("/text");
		} catch {
			setFehlerMeldung("Löschen fehlgeschlagen.");
		}
	};

	const showLoadingIndicator =
		ladeVorgang || (pruefRaumQuery?.hatPasswort && !zugangGenehmigt);

	return (
		<PageLayout backUrl="/text" title="TextShare" toolId="textshare">
			<Toast
				message={erfolgsMeldung || ""}
				onClose={() => setErfolgsMeldung(null)}
				visible={!!erfolgsMeldung}
			/>
			<Toast
				message={fehlerMeldung || ""}
				onClose={() => setFehlerMeldung(null)}
				type="error"
				visible={!!fehlerMeldung}
			/>

			<div className="space-y-4">
				{
					showLoadingIndicator && !ablaufDialogAnzeigen ? (
						<div className="flex items-center justify-center py-20">
							<div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
						</div>
					) : zugangGenehmigt && raumQuery ? (
						<>
							<RaumStatusLeiste
								istBesitzer={istBesitzer}
								nurBesitzerEdit={nurBesitzerEdit}
								onDeleteClick={istBesitzer ? handleDeleteClick : undefined}
								onEinstellungenClick={() => setEinstellungenModalAnzeigen(true)}
								onLinkKopieren={linkKopieren}
								onNurBesitzerEditClick={handleToggleNurBesitzerEdit}
								onQrAnzeigen={handleQrAnzeigen}
								passwort={raumQuery.hatPasswort ? "geschützt" : undefined}
								raumId={raumId}
							/>
							<RaumEditor
								apiKey={TINYMCE_API_KEY}
								inhalt={inhalt}
								kannBearbeiten={kannBearbeiten}
								ladeVorgang={ladeVorgang}
								onInhaltAendern={setInhalt} // Pass setter directly
								onSpeichern={handleInhaltSpeichern}
								speicherStatus={speicherStatus}
							/>
						</>
					) : null /* Don't render editor if access not granted */
				}
			</div>

			<RaumPasswort
				fehler={fehlerMeldung}
				isOpen={passwortModalAnzeigen}
				onClose={() => {
					setPasswortModalAnzeigen(false);
					if (!zugangGenehmigt) navigate("/text"); // Go back if password was required but not entered
				}}
				onPasswortChange={setEingegebenesPasswort}
				onZugreifen={handlePasswortPruefen}
				passwort={eingegebenesPasswort} // Show password-specific errors here
			/>

			<RaumEinstellungen
				isOpen={einstellungenModalAnzeigen}
				nurBesitzerEdit={nurBesitzerEdit}
				onClose={() => setEinstellungenModalAnzeigen(false)} // Use state holding the actual/new password
				onNurBesitzerEditChange={setNurBesitzerEdit} // Allow changing the password
				onPasswortChange={setPasswort}
				onSpeichern={handleEinstellungenAktualisieren}
				passwort={passwort}
			/>

			<AlertDialog
				onOpenChange={(open) => !open && handleAblaufRoom()}
				open={ablaufDialogAnzeigen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Raum nicht verfügbar</AlertDialogTitle>
						<AlertDialogDescription>
							Der angeforderte TextShare-Raum existiert nicht.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogAction onClick={handleAblaufRoom}>
							Zurück zur Übersicht
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<QRCodeModal
				isOpen={qrModalOpen}
				link={qrLink}
				onClose={() => setQrModalOpen(false)}
				title="QR-Code für TextShare-Raum"
			/>
		</PageLayout>
	);
}
